import { Grid, Table, Typography, theme } from 'antd';
import { Link } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import type { ClienteSaldo } from '@aid/shared';
import { EmptyState } from '../../../components/EmptyState';
import { formatCurrency } from '../../../lib/format';
import { useT } from '../../../locales';

const { Text } = Typography;

/** Fuente monoespaciada para IPs/valores técnicos, consistente en toda la tabla. */
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, monospace';

interface SaldosTableProps {
  data: ClienteSaldo[];
}

export function SaldosTable({ data }: SaldosTableProps) {
  const t = useT();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const columns: ColumnsType<ClienteSaldo> = [
    {
      title: t('dashboard.saldosIndex'),
      key: 'index',
      width: 56,
      render: (_: unknown, __: unknown, idx: number) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {idx + 1}
        </Text>
      ),
    },
    {
      title: t('dashboard.saldosCliente'),
      dataIndex: 'nombre',
      key: 'nombre',
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      render: (val: string, record: ClienteSaldo) => (
        <div style={{ minWidth: 0 }}>
          <Link
            to={`/clientes/${record.id}`}
            aria-label={t('dashboard.verCuenta')}
            style={{ display: 'block' }}
          >
            <Text
              style={{ fontWeight: 500, display: 'block' }}
              ellipsis={isMobile ? false : { tooltip: val }}
            >
              {val}
            </Text>
          </Link>
          {isMobile && (
            <Text type="secondary" style={{ fontSize: 12, lineHeight: '16px', display: 'block' }}>
              {record.ip ? (
                <span
                  style={{
                    fontFamily: MONO_FONT,
                  }}
                >
                  {record.ip}
                </span>
              ) : (
                '—'
              )}
              {' · '}
              {record.fechaCobro ?? '—'}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: t('dashboard.saldosIp'),
      dataIndex: 'ip',
      key: 'ip',
      responsive: ['md'],
      render: (val: string | null) =>
        val ? (
          <Text style={{ fontFamily: MONO_FONT, fontSize: 12 }}>{val}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('dashboard.saldosUltimoCobro'),
      dataIndex: 'fechaCobro',
      key: 'fechaCobro',
      responsive: ['md'],
      render: (val: string | null) =>
        val ? <Text style={{ fontSize: 13 }}>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: t('dashboard.saldosDeudaPendiente'),
      dataIndex: 'deudaPendiente',
      key: 'deudaPendiente',
      align: 'right',
      render: (val: string) => {
        // billing-domain-rules (Phase 8.4): the column renders the deudaPendiente
        // (open-invoice remainder) — non-negative. Positive = debtor, 0 = no
        // debt. Color follows the magnitude, not the sign. NEVER netted with
        // saldo a favor — see spec: client-balance-reporting.
        const num = Number.parseFloat(val);
        const positive = num > 0;
        const neutral = num === 0;
        const color = positive
          ? token.colorError
          : neutral
            ? token.colorTextSecondary
            : token.colorSuccess;
        return (
          <Text
            strong
            style={{
              color,
              fontSize: isMobile ? 13 : undefined,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCurrency(val)}
          </Text>
        );
      },
      sorter: (a, b) =>
        Number.parseFloat(a.deudaPendiente ?? '0') - Number.parseFloat(b.deudaPendiente ?? '0'),
      defaultSortOrder: 'descend',
    },
    // billing-domain-rules (Phase 8.4): saldo a favor surfaced as a separate
    // column to keep deuda and credit distinct without netting.
    {
      title: t('dashboard.saldosSaldoAFavor'),
      dataIndex: 'saldoAFavor',
      key: 'saldoAFavor',
      align: 'right',
      render: (val: string) => {
        const num = Number.parseFloat(val);
        const positive = num > 0;
        const color = positive ? token.colorSuccess : token.colorTextSecondary;
        return (
          <Text
            strong
            style={{
              color,
              fontSize: isMobile ? 13 : undefined,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCurrency(val)}
          </Text>
        );
      },
      sorter: (a, b) =>
        Number.parseFloat(a.saldoAFavor ?? '0') - Number.parseFloat(b.saldoAFavor ?? '0'),
    },
  ];

  return (
    <Table<ClienteSaldo>
      dataSource={data}
      columns={columns}
      rowKey="id"
      // The backend now returns the full debtor list (no LIMIT), so the tab
      // paginates client-side instead of rendering every row at once.
      pagination={{
        defaultPageSize: 20,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
      }}
      size="middle"
      locale={{
        emptyText: (
          <EmptyState
            compact
            title={t('dashboard.saldosSinDeudores')}
            description={t('dashboard.saldosSinDeudoresDesc')}
          />
        ),
      }}
    />
  );
}
